/* eslint-disable no-alert */
import React, { useEffect, useMemo, useState } from 'react';
import Loader from 'components/atoms/loader';
import EmptyState from 'components/atoms/empty_state';
import ContractAnalysisItem from 'components/organisms/contract_analysis_Item';
import ContractAnalysisForm from 'components/organisms/contract_analysis_form';
import ContractAnalysisReport from 'components/organisms/contract_analysis_report';
import { useContractValidations } from 'hooks/useContractValidations';
import {
  ContractValidationPayload,
  createContractValidation,
  getContractValidation,
} from 'services/actions/contract_validations';
import { BidderFileGeneratedHandler } from 'services/actions/ai_management_files';
import createTrack from 'services/actions/tracker_actions';
import { BiddingData } from 'store/modules/bidding_store/types';
import { BiddingItem, ContractsAnalysisItemTypes } from 'store/modules/contract_analysis/types';

interface AiManagementContractAnalysisProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  onFileGenerated: BidderFileGeneratedHandler;
}

type View = { name: 'list' } | { name: 'new' } | { name: 'report'; id: string };

const AiManagementContractAnalysis: React.FC<AiManagementContractAnalysisProps> = ({
  bidding,
  loading,
  error,
  onFileGenerated,
}) => {
  const [view, setView] = useState<View>({ name: 'list' });
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<{
    id: string;
    analysis: ContractsAnalysisItemTypes | null;
    error: boolean;
  } | null>(null);

  const conlicitacaoId = bidding?.bidding_id ? Number(bidding.bidding_id) : null;
  const validations = useContractValidations(conlicitacaoId);

  const biddingInfo = useMemo<BiddingItem | null>(
    () =>
      bidding && conlicitacaoId
        ? {
            id: conlicitacaoId,
            edital: bidding.edital,
            modalidade: bidding.modality?.nome,
            orgao_nome: bidding.orgao_nome,
          }
        : null,
    [bidding, conlicitacaoId],
  );

  const reportId = view.name === 'report' ? view.id : null;

  useEffect(() => {
    if (!reportId) return undefined;

    let active = true;
    setReport({ id: reportId, analysis: null, error: false });

    getContractValidation(reportId)
      .then((response) => {
        if (active) setReport({ id: reportId, analysis: response.data, error: false });
      })
      .catch(() => {
        if (active) setReport({ id: reportId, analysis: null, error: true });
      });

    return () => {
      active = false;
    };
  }, [reportId]);

  const handleSubmit = (payload: ContractValidationPayload) => {
    setSubmitting(true);

    createContractValidation(payload)
      .then(() => {
        createTrack('Enviou contrato para análise', { fonte: 'Gestão IA' });
        validations.reload();
        setView({ name: 'list' });
      })
      .catch(() => alert('Não foi possível enviar os documentos. Tente novamente.'))
      .finally(() => setSubmitting(false));
  };

  if (loading && !bidding) return <Loader />;
  if (error || !bidding)
    return <EmptyState icon="error_outline" title="Não foi possível carregar a licitação." />;

  const backButton = (
    <button
      type="button"
      className="btn btn-link p-0 btn-sm d-flex align-items-center mb-3"
      onClick={() => setView({ name: 'list' })}
    >
      <i className="material-icons mr-1">arrow_back_ios</i>
      Voltar
    </button>
  );

  if (view.name === 'new')
    return (
      <>
        {backButton}
        <p className="text-muted mb-4">
          Envie a minuta do edital e o contrato recebido para validar a conformidade entre eles.
        </p>
        <ContractAnalysisForm
          key={conlicitacaoId ?? undefined}
          bidding={biddingInfo}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      </>
    );

  if (view.name === 'report') {
    const current = report?.id === view.id ? report : null;

    return (
      <>
        {backButton}
        {current?.error && (
          <EmptyState icon="error_outline" title="Não foi possível carregar a análise." />
        )}
        {!current?.error && !current?.analysis && <Loader />}
        {current?.analysis && (
          <ContractAnalysisReport
            key={view.id}
            analysis={current.analysis}
            onFileGenerated={onFileGenerated}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <p className="text-muted mb-0 mr-3">
          Valide a conformidade entre a minuta de contrato presente no edital e o contrato recebido
          da Administração Pública.
        </p>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center flex-shrink-0"
          onClick={() => setView({ name: 'new' })}
        >
          <i className="material-icons">add</i>
          Nova Análise
        </button>
      </div>

      {validations.loading && <Loader />}
      {validations.error && (
        <EmptyState icon="error_outline" title="Não foi possível carregar as análises." />
      )}
      {!validations.loading && !validations.error && validations.validations.length === 0 && (
        <EmptyState
          icon="description"
          title="Nenhuma análise de contrato para esta licitação."
          description="Clique em Nova Análise para enviar o contrato recebido."
        />
      )}
      {validations.validations.length > 0 && (
        <div className="card">
          <div className="list-group list-group-flush">
            {validations.validations.map((contract) => (
              <ContractAnalysisItem
                key={contract.id}
                contract={contract}
                onView={(id) => setView({ name: 'report', id })}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AiManagementContractAnalysis;
